<script lang="ts">
	import { onMount } from 'svelte';
	import Button from '$lib/components/primitives/Button.svelte';
	import Icon from '$lib/components/primitives/Icon.svelte';
	import HeroSkeleton from '$lib/components/skeletons/HeroSkeleton.svelte';
	import { _ } from 'svelte-i18n';
	import { loading, setLoading, isCriticalContentLoaded } from '$lib/stores/loading.ts';

	let { primaryHref = 'https://app.heyweek.com/?signup' } = $props();

	// Use reactive translations directly
	const displayTitle = $derived($_('hero.title'));
	const displaySubtitle = $derived($_('hero.subtitle'));
	const displayPrimaryCTA = $derived($_('hero.cta.primary'));
	const displaySecondaryCTA = $derived($_('hero.cta.secondary'));
	const displayNoCard = $derived($_('hero.noCard'));
	const displayBetaBadge = $derived($_('hero.beta.badge'));
	const displayBetaDisclaimer = $derived($_('hero.beta.disclaimer'));

	// Track loading state
	let isContentReady = $state(false);

	// Track which app screenshot to show based on the active landing-page theme
	let isDark = $state(true);

	// Mouse-tracking 3D tilt for the screenshot frame
	let tiltX = $state(3); // default: slight lean back
	let tiltY = $state(0);
	let isHovering = $state(false);

	function onFrameMouseMove(e: MouseEvent) {
		const el = e.currentTarget as HTMLElement;
		const r = el.getBoundingClientRect();
		const nx = (e.clientX - r.left) / r.width - 0.5; // -0.5 … 0.5
		const ny = (e.clientY - r.top) / r.height - 0.5;
		tiltX = -ny * 10; // top cursor → lean back, bottom → lean forward
		tiltY = nx * 14; // left cursor → turn left, right → turn right
	}

	function onFrameMouseEnter() {
		isHovering = true;
	}

	function onFrameMouseLeave() {
		isHovering = false;
		tiltX = 3;
		tiltY = 0;
	}

	onMount(() => {
		// Check if fonts are loaded
		if ('fonts' in document) {
			(document as any).fonts.ready.then(() => {
				setLoading('fonts', false);
				isContentReady = true;
			});
		} else {
			setLoading('fonts', false);
			isContentReady = true;
		}

		// Set translations and hero as loaded
		setLoading('translations', false);
		setLoading('hero', false);

		// Read initial theme from the <html data-theme> attribute
		const readTheme = () => {
			isDark = document.documentElement.getAttribute('data-theme') !== 'light';
		};
		readTheme();

		// Watch for theme changes (the theme store updates data-theme on <html>)
		const observer = new MutationObserver(readTheme);
		observer.observe(document.documentElement, {
			attributes: true,
			attributeFilter: ['data-theme']
		});
		return () => observer.disconnect();
	});

	// Show content immediately if translations are available
	const showContent = $derived(displayTitle !== undefined && displayTitle !== '');
</script>

{#if showContent}
	<section class="hero">
		<div class="container">
			<div class="hero__content" class:hero__content--loaded={showContent}>
				<h1 class="hero__title">{displayTitle}</h1>
				<p class="hero__subtitle">{displaySubtitle}</p>

				<div class="hero__actions">
					<Button size="lg" variant="primary" href={primaryHref}>
						{displayPrimaryCTA}
					</Button>
					<a class="hero__secondary-cta" href="#features">
						{displaySecondaryCTA} →
					</a>
				</div>
				<p class="hero__no-card">{displayNoCard}</p>

				<div class="hero__feature-pills">
					<span class="hero__pill">✓ Time tracking</span>
					<span class="hero__pill">✓ Invoicing</span>
					<span class="hero__pill">✓ Cashflow</span>
					<span class="hero__pill">✓ Projects</span>
					<span class="hero__pill">✓ Tasks & Boards</span>
					<span class="hero__pill">✓ Notes & Files</span>
					<span class="hero__pill">✓ Team pulse</span>
					<span class="hero__pill">✓ Absence</span>
					<span class="hero__pill">✓ Reports</span>
				</div>

				<div class="hero__visual">
					<div
						class="hero__screenshot-frame"
						class:is-hovering={isHovering}
						style="transform: perspective(1200px) rotateX({tiltX}deg) rotateY({tiltY}deg) translateY({isHovering
							? -6
							: 0}px) scale({isHovering ? 1.01 : 1});"
						onmousemove={onFrameMouseMove}
						onmouseenter={onFrameMouseEnter}
						onmouseleave={onFrameMouseLeave}
						role="img"
						aria-label="Heyweek app preview"
					>
						<div class="hero__screenshot-chrome">
							<span class="chrome-dot"></span>
							<span class="chrome-dot"></span>
							<span class="chrome-dot"></span>
						</div>
						<!-- Preload both so the crossfade is instant -->
						<link rel="preload" as="image" href="/images/app-dark.png" />
						<link rel="preload" as="image" href="/images/app-light.png" />
						<div class="hero__screenshot-images">
							<img
								src="/images/app-dark.png"
								alt="Heyweek app — dark theme"
								class="hero__screenshot-img"
								class:hero__screenshot-img--visible={isDark}
								width="1280"
								height="430"
								loading="eager"
							/>
							<img
								src="/images/app-light.png"
								alt="Heyweek app — light theme"
								class="hero__screenshot-img"
								class:hero__screenshot-img--visible={!isDark}
								width="1280"
								height="430"
								loading="eager"
							/>
						</div>
					</div>
					<div class="hero__visual-fade" aria-hidden="true"></div>
					<div class="hero__beta-badge">
						<span class="hero__beta-text">{displayBetaBadge}</span>
						<p class="hero__beta-disclaimer">
							{displayBetaDisclaimer}
						</p>
					</div>
				</div>
			</div>
		</div>
	</section>
{:else}
	<HeroSkeleton />
{/if}

<style>
	.hero {
		position: relative;
		padding-block: clamp(var(--space-12), 6vw, var(--space-20));
		overflow: hidden;
	}

	.container {
		position: relative;
	}

	.hero__content {
		max-width: 800px;
		margin-inline: auto;
		text-align: center;
		opacity: 0;
		transform: translateY(10px);
		transition:
			opacity 0.3s ease,
			transform 0.3s ease;
	}

	.hero__content--loaded {
		opacity: 1;
		transform: translateY(0);
	}

	.hero__title {
		margin-bottom: var(--space-4);
		background: linear-gradient(135deg, var(--text-primary) 0%, var(--text-brand) 100%);
		-webkit-background-clip: text;
		-webkit-text-fill-color: transparent;
		background-clip: text;
		animation: fadeInUp 0.8s ease-out;
	}

	.hero__subtitle {
		font-size: var(--font-size-lg);
		color: var(--text-secondary);
		max-width: 60ch;
		margin-inline: auto;
		margin-bottom: var(--space-6);
		line-height: var(--line-height-normal);
		animation: fadeInUp 0.8s ease-out 0.1s backwards;
	}

	.hero__actions {
		display: flex;
		align-items: center;
		justify-content: center;
		gap: var(--space-4);
		margin-bottom: var(--space-3);
		animation: fadeInUp 0.8s ease-out 0.2s backwards;
		flex-wrap: wrap;
	}

	.hero__secondary-cta {
		display: inline-flex;
		align-items: center;
		min-height: 44px;
		padding: 0 var(--space-2);
		font-size: var(--font-size-base);
		font-weight: var(--font-weight-medium);
		color: var(--text-secondary);
		text-decoration: none;
		transition: color 0.2s ease;
		white-space: nowrap;
	}

	.hero__secondary-cta:hover {
		color: var(--text-primary);
	}

	.hero__no-card {
		font-size: var(--font-size-sm);
		color: var(--text-tertiary, var(--text-secondary));
		opacity: 0.7;
		margin-bottom: var(--space-6);
		animation: fadeInUp 0.8s ease-out 0.25s backwards;
	}

	.hero__feature-pills {
		display: flex;
		flex-wrap: wrap;
		justify-content: center;
		gap: var(--space-2);
		margin-bottom: var(--space-8);
		animation: fadeInUp 0.8s ease-out 0.3s backwards;
	}

	.hero__pill {
		font-size: var(--font-size-xs);
		font-weight: var(--font-weight-medium);
		color: var(--text-secondary);
		background: var(--bg-secondary);
		border: 1px solid var(--border-light);
		border-radius: var(--radius-full);
		padding: var(--space-1) var(--space-3);
		white-space: nowrap;
		transition:
			color 0.2s,
			border-color 0.2s;
	}

	.hero__pill:hover {
		color: var(--text-primary);
		border-color: var(--border-default);
	}

	/* Visual — real app screenshot, theme-adaptive */
	.hero__visual {
		margin-top: var(--space-10);
		animation: fadeInUp 0.8s ease-out 0.4s backwards;
		width: min(900px, 100vw - 2rem);
		margin-inline: auto;
		position: relative;
	}

	/* Soft brand glow behind the card */
	.hero__visual::before {
		content: '';
		position: absolute;
		inset: 15% 10% -8%;
		background: radial-gradient(
			ellipse at 50% 70%,
			hsla(var(--brand-h, 340), 70%, 55%, 0.2) 0%,
			transparent 68%
		);
		filter: blur(48px);
		pointer-events: none;
	}

	/* Single browser-chrome frame */
	.hero__screenshot-frame {
		position: relative;
		border-radius: 12px;
		overflow: hidden;
		border: 1px solid var(--border-light);
		box-shadow:
			0 2px 0 hsla(0, 0%, 100%, 0.06) inset,
			0 24px 80px hsla(0, 0%, 0%, 0.3),
			0 8px 24px hsla(0, 0%, 0%, 0.15);
		/* Springy return when mouse leaves */
		transition:
			transform 0.7s cubic-bezier(0.34, 1.56, 0.64, 1),
			box-shadow 0.5s ease;
		will-change: transform;
		cursor: default;
		/* Ensure the 3D-transformed frame doesn't visually bleed into the badge below */
		margin-bottom: var(--space-6);
	}

	/* Fast follow while mouse is moving over the card */
	.hero__screenshot-frame.is-hovering {
		transition:
			transform 0.08s ease-out,
			box-shadow 0.2s ease;
		box-shadow:
			0 2px 0 hsla(0, 0%, 100%, 0.08) inset,
			0 32px 100px hsla(0, 0%, 0%, 0.38),
			0 12px 32px hsla(0, 0%, 0%, 0.22);
	}

	/* Fake browser chrome bar */
	.hero__screenshot-chrome {
		display: flex;
		align-items: center;
		gap: 6px;
		padding: 9px 14px;
		background: var(--bg-secondary);
		border-bottom: 1px solid var(--border-light);
		flex-shrink: 0;
	}

	.chrome-dot {
		width: 11px;
		height: 11px;
		border-radius: 50%;
		flex-shrink: 0;
	}

	.chrome-dot:nth-child(1) {
		background: hsl(0 75% 58%);
	}
	.chrome-dot:nth-child(2) {
		background: hsl(38 82% 56%);
	}
	.chrome-dot:nth-child(3) {
		background: hsl(130 52% 46%);
	}

	/* Spacer that separates the frame from the beta badge */
	.hero__visual-fade {
		display: none;
	}

	/* Image container — stacks both images for crossfade */
	.hero__screenshot-images {
		position: relative;
		aspect-ratio: 1280 / 430;
	}

	.hero__screenshot-img {
		display: block;
		position: absolute;
		inset: 0;
		width: 100%;
		height: 100%;
		object-fit: cover;
		object-position: top left;
		opacity: 0;
		transition: opacity 0.4s ease;
	}

	/* Svelte class:x binding toggles this */
	.hero__screenshot-img--visible {
		opacity: 1;
	}

	/* Beta Badge */
	.hero__beta-badge {
		display: flex;
		flex-direction: column;
		align-items: center;
		justify-content: center;
		gap: var(--space-2);
		animation: fadeInUp 0.8s ease-out 0.5s backwards;
		position: relative;
		z-index: 10;
	}

	.hero__beta-text {
		display: inline-block;
		padding: var(--space-1) var(--space-3);
		font-size: var(--font-size-xs);
		font-weight: 600;
		letter-spacing: 0.08em;
		color: var(--brand-primary);
		background: linear-gradient(
			135deg,
			hsla(var(--brand-h), 70%, 50%, 0.08) 0%,
			hsla(var(--brand-h), 70%, 60%, 0.04) 100%
		);
		border: 1px solid hsla(var(--brand-h), 70%, 50%, 0.2);
		border-radius: var(--radius-full);
		backdrop-filter: blur(8px);
		transition: all 0.2s ease;
	}

	.hero__beta-text:hover {
		background: linear-gradient(
			135deg,
			hsla(var(--brand-h), 70%, 50%, 0.12) 0%,
			hsla(var(--brand-h), 70%, 60%, 0.06) 100%
		);
		border-color: hsla(var(--brand-h), 70%, 50%, 0.3);
		transform: scale(1.05);
	}

	.hero__beta-disclaimer {
		font-size: var(--font-size-sm);
		color: var(--text-secondary);
		max-width: 420px;
		text-align: center;
		line-height: 1.5;
		opacity: 0.8;
		font-style: italic;
		animation: fadeInUp 0.8s ease-out 0.6s backwards;
	}

	/* Animations */
	@keyframes fadeInUp {
		from {
			opacity: 0;
			transform: translateY(20px);
		}
		to {
			opacity: 1;
			transform: translateY(0);
		}
	}

	/* Responsive */
	@media (max-width: 768px) {
		.hero__screenshot-frame {
			transform: perspective(800px) rotateX(2deg);
			border-radius: 8px;
		}
		.hero__screenshot-frame:hover {
			transform: perspective(800px) rotateX(0deg) translateY(-2px);
		}
	}
</style>
